import { Injectable } from '@angular/core';
import {
  CropperPosition,
  Dimensions,
  ImageCroppedEvent,
  ImageTransform,
  ExifTransform,
} from '../interfaces/image-cropper.interfaces';
import { resizeCanvas } from '../utils/image-cropper';

@Injectable({
  providedIn: 'root',
})
export class CropOperationsService {
  /**
   * Calculates the image position based on cropper position and scaling
   */
  private getImagePosition(
    cropper: CropperPosition,
    transformedSize: Dimensions,
    sourceImageElement: HTMLElement,
    containWithinAspectRatio: boolean
  ): CropperPosition {
    const ratio: number = transformedSize.width / sourceImageElement.offsetWidth;

    const out: CropperPosition = {
      x1: Math.round(cropper.x1 * ratio),
      y1: Math.round(cropper.y1 * ratio),
      x2: Math.round(cropper.x2 * ratio),
      y2: Math.round(cropper.y2 * ratio),
    };

    if (!containWithinAspectRatio) {
      out.x1 = Math.max(out.x1, 0);
      out.y1 = Math.max(out.y1, 0);
      out.x2 = Math.min(out.x2, transformedSize.width);
      out.y2 = Math.min(out.y2, transformedSize.height);
    }

    return out;
  }

  /**
   * Calculates the offset image position for contained aspect ratio
   */
  private getOffsetImagePosition(
    cropper: CropperPosition,
    transformedSize: Dimensions,
    sourceImageElement: HTMLElement,
    originalSize: Dimensions,
    canvasRotation: number,
    exifTransform: ExifTransform,
    containWithinAspectRatio: boolean
  ): CropperPosition {
    const totalRotation: number = canvasRotation + exifTransform.rotate;
    const ratio: number = transformedSize.width / sourceImageElement.offsetWidth;
    let offsetX: number;
    let offsetY: number;

    if (totalRotation % 2) {
      offsetX = (transformedSize.width - originalSize.height) / 2;
      offsetY = (transformedSize.height - originalSize.width) / 2;
    } else {
      offsetX = (transformedSize.width - originalSize.width) / 2;
      offsetY = (transformedSize.height - originalSize.height) / 2;
    }

    const out: CropperPosition = {
      x1: Math.round(cropper.x1 * ratio) - offsetX,
      y1: Math.round(cropper.y1 * ratio) - offsetY,
      x2: Math.round(cropper.x2 * ratio) - offsetX,
      y2: Math.round(cropper.y2 * ratio) - offsetY,
    };

    if (!containWithinAspectRatio) {
      out.x1 = Math.max(out.x1, 0);
      out.y1 = Math.max(out.y1, 0);
      out.x2 = Math.min(out.x2, transformedSize.width);
      out.y2 = Math.min(out.y2, transformedSize.height);
    }

    return out;
  }

  /**
   * Performs the actual cropping operation
   */
  public cropImage(
    cropper: CropperPosition,
    transformedImage: HTMLImageElement,
    transformedSize: Dimensions,
    sourceImageElement: HTMLElement,
    transform: ImageTransform,
    backgroundColor: string,
    format: string,
    imageQuality: number,
    containWithinAspectRatio: boolean,
    originalSize: Dimensions,
    canvasRotation: number,
    exifTransform: ExifTransform,
    resizeToWidth: number,
    resizeToHeight: number,
    maintainAspectRatio: boolean,
    aspectRatio: number,
    onlyScaleDown: boolean
  ): ImageCroppedEvent | null {
    const imagePosition: CropperPosition = this.getImagePosition(
      cropper,
      transformedSize,
      sourceImageElement,
      containWithinAspectRatio
    );
    const width: number = imagePosition.x2 - imagePosition.x1;
    const height: number = imagePosition.y2 - imagePosition.y1;

    const cropCanvas: HTMLCanvasElement | null = this.createCropCanvas(width, height, backgroundColor);
    if (!cropCanvas) {
      return null;
    }

    this.drawImageOnCanvas(cropCanvas, transformedImage, imagePosition, transformedSize, transform);

    const output: ImageCroppedEvent = this.createImageCroppedEvent(
      width,
      height,
      imagePosition,
      cropper,
      containWithinAspectRatio,
      cropper,
      transformedSize,
      sourceImageElement,
      originalSize,
      canvasRotation,
      exifTransform
    );

    this.applyResizing(
      output,
      width,
      height,
      resizeToWidth,
      resizeToHeight,
      onlyScaleDown,
      maintainAspectRatio,
      aspectRatio,
      cropCanvas
    );

    const quality: number = Math.min(1, Math.max(0, imageQuality / 100));
    output.base64 = cropCanvas.toDataURL('image/' + format, quality);
    return output;
  }

  private createCropCanvas(width: number, height: number, backgroundColor: string): HTMLCanvasElement | null {
    const cropCanvas: HTMLCanvasElement = document.createElement('canvas');
    cropCanvas.width = width;
    cropCanvas.height = height;

    const ctx: CanvasRenderingContext2D | null = cropCanvas.getContext('2d');
    if (!ctx) {
      return null;
    }

    if (backgroundColor != null) {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, width, height);
    }

    return cropCanvas;
  }

  private drawImageOnCanvas(
    cropCanvas: HTMLCanvasElement,
    transformedImage: HTMLImageElement,
    imagePosition: CropperPosition,
    transformedSize: Dimensions,
    transform: ImageTransform
  ): void {
    const ctx: CanvasRenderingContext2D = cropCanvas.getContext('2d');
    const scaleX: number = (transform.scale || 1) * (transform.flipH ? -1 : 1);
    const scaleY: number = (transform.scale || 1) * (transform.flipV ? -1 : 1);

    ctx.setTransform(scaleX, 0, 0, scaleY, transformedSize.width / 2, transformedSize.height / 2);
    ctx.translate(-imagePosition.x1 / scaleX, -imagePosition.y1 / scaleY);
    ctx.rotate(((transform.rotate || 0) * Math.PI) / 180);
    ctx.drawImage(transformedImage, -transformedSize.width / 2, -transformedSize.height / 2);
  }

  private createImageCroppedEvent(
    width: number,
    height: number,
    imagePosition: CropperPosition,
    cropper: CropperPosition,
    containWithinAspectRatio: boolean,
    cropper2: CropperPosition,
    transformedSize: Dimensions,
    sourceImageElement: HTMLElement,
    originalSize: Dimensions,
    canvasRotation: number,
    exifTransform: ExifTransform
  ): ImageCroppedEvent {
    const output: ImageCroppedEvent = {
      width,
      height,
      imagePosition,
      cropperPosition: { ...cropper },
    };

    if (containWithinAspectRatio) {
      output.offsetImagePosition = this.getOffsetImagePosition(
        cropper2,
        transformedSize,
        sourceImageElement,
        originalSize,
        canvasRotation,
        exifTransform,
        containWithinAspectRatio
      );
    }

    return output;
  }

  private applyResizing(
    output: ImageCroppedEvent,
    width: number,
    height: number,
    resizeToWidth: number,
    resizeToHeight: number,
    onlyScaleDown: boolean,
    maintainAspectRatio: boolean,
    aspectRatio: number,
    cropCanvas: HTMLCanvasElement
  ): void {
    const resizeRatio: number = this.getResizeRatio(width, height, resizeToWidth, resizeToHeight, onlyScaleDown);
    if (resizeRatio !== 1) {
      output.width = Math.round(width * resizeRatio);
      output.height = maintainAspectRatio ? Math.round(output.width / aspectRatio) : Math.round(height * resizeRatio);
      resizeCanvas(cropCanvas, output.width, output.height);
    }
  }

  /**
   * Calculates resize ratio based on target dimensions
   */
  private getResizeRatio(
    width: number,
    height: number,
    resizeToWidth: number,
    resizeToHeight: number,
    onlyScaleDown: boolean
  ): number {
    const ratioWidth: number = resizeToWidth / width;
    const ratioHeight: number = resizeToHeight / height;
    const ratios: number[] = new Array<number>();

    if (resizeToWidth > 0) {
      ratios.push(ratioWidth);
    }
    if (resizeToHeight > 0) {
      ratios.push(ratioHeight);
    }

    const result: number = ratios.length === 0 ? 1 : Math.min(...ratios);

    if (result > 1 && !onlyScaleDown) {
      return result;
    }
    return Math.min(result, 1);
  }
}
